import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import PropTypes from "prop-types";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import FormControl from "@mui/material/FormControl";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import LinearProgress from "@mui/material/LinearProgress";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { get, put } from "lib/api";
import InfoCard from "mobile/components/InfoCard";
import MobileTopBar from "mobile/components/MobileTopBar";
import { IosCard, IosPage, iosColors } from "mobile/components/IosParity";

const normalize = (row) =>
  row?.attributes ? { id: row.id, documentId: row.documentId, ...row.attributes } : row;

const normalizeRelation = (value) => {
  if (!value) return null;
  if (value?.data) return normalizeRelation(value.data);
  return normalize(value);
};

const normalizeList = (value) => {
  const rows = value?.data || value || [];
  return Array.isArray(rows) ? rows.map(normalize).filter(Boolean) : [];
};

const formatMoney = (value, currency = "USD") => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(n);
  } catch {
    return `$${n.toFixed(2)}`;
  }
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString();
};

const STATUS_OPTIONS = ["draft", "open", "confirmed", "in_progress", "ready_to_ship", "shipped", "paid", "cancelled"];

const primaryActionSx = {
  bgcolor: "#B54708",
  color: "#fff",
  borderRadius: 1,
  minHeight: 44,
  fontWeight: 900,
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { bgcolor: "#953A05", boxShadow: "none" },
};

const secondaryActionSx = {
  color: "#8F3700",
  borderColor: "rgba(181,71,8,0.38)",
  borderRadius: 1,
  minHeight: 44,
  fontWeight: 900,
  textTransform: "none",
};

export default function SalesOrderDetailMobile({ mode = "sales" }) {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [draft, setDraft] = useState({
    status: "open",
    shipBy: "",
    shippingCarrier: "",
    trackingNumber: "",
    notes: "",
  });

  const isInvoice = mode === "invoice";

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ["m-sales-order-detail", documentId],
    queryFn: () =>
      get(`/api/sales-orders/${documentId}`, {
        populate: {
          attachments: true,
          customer: { populate: { addresses: true, fiscalAddress: true } },
          workOrder: true,
          items: {
            populate: {
              inventoryItem: { populate: { part: true, images: true } },
              units: { populate: { finishedGood: true, workOrder: true } },
            },
          },
        },
      }),
    enabled: !!documentId,
    staleTime: 10_000,
  });

  const order = useMemo(() => (data?.data ? normalize(data.data) : null), [data]);
  const customer = useMemo(() => normalizeRelation(order?.customer), [order]);
  const workOrder = useMemo(() => normalizeRelation(order?.workOrder), [order]);
  const items = useMemo(() => normalizeList(order?.items), [order]);
  const currency = order?.currency || "USD";

  useEffect(() => {
    if (!order) return;
    setDraft({
      status: order.status || "open",
      shipBy: order.shipBy ? String(order.shipBy).slice(0, 10) : "",
      shippingCarrier: order.shippingCarrier || "",
      trackingNumber: order.trackingNumber || "",
      notes: order.notes || "",
    });
  }, [order, editOpen]);

  const saveMutation = useMutation({
    mutationFn: () =>
      put(`/api/sales-orders/${order?.documentId || documentId}`, {
        data: {
          status: draft.status || "open",
          shipBy: draft.shipBy || null,
          shippingCarrier: draft.shippingCarrier || null,
          trackingNumber: draft.trackingNumber || null,
          notes: draft.notes || null,
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["m-sales-order-detail", documentId] });
      queryClient.invalidateQueries({ queryKey: ["m-sales-orders"] });
      queryClient.invalidateQueries({ queryKey: ["m-invoices"] });
      setEditOpen(false);
    },
  });

  if (isLoading) return <LinearProgress />;

  if (error || !order) {
    return (
      <IosPage>
        <MobileTopBar title={isInvoice ? "Invoice" : "Sales Order"} onBack={() => navigate(isInvoice ? "/m/invoices" : "/m/sales")} />
        <Typography sx={{ color: iosColors.red, fontWeight: 850, mt: 2 }}>
          {isInvoice ? "Invoice not found." : "Sales order not found."}
        </Typography>
      </IosPage>
    );
  }

  const subtotal = order.total ?? order.subtotal;
  const total = order.totalWithTax ?? order.grandTotal ?? order.total;

  return (
    <IosPage>
      <MobileTopBar
        title={isInvoice ? "Invoice" : "Sales Order"}
        subtitle={order.salesOrderNumber || ""}
        onBack={() => navigate(isInvoice ? "/m/invoices" : "/m/sales")}
        right={
          <Stack direction="row" spacing={0.5} alignItems="center">
            {isFetching ? <Chip size="small" variant="outlined" label="Syncing" /> : null}
            <IconButton aria-label="Edit order" size="small" onClick={() => setEditOpen(true)}>
              <Icon fontSize="small">edit</Icon>
            </IconButton>
          </Stack>
        }
      />

      <Stack spacing={0.75} sx={{ mt: 1 }}>
        <InfoCard
          title="Summary"
          rows={[
            { label: isInvoice ? "Invoice #" : "Order #", value: order.salesOrderNumber || "—" },
            { label: "Status", value: String(order.status || "open").replaceAll("_", " ") },
            { label: "Customer", value: customer?.name || "—" },
            { label: "Ship by", value: formatDate(order.shipBy) },
            { label: "Document type", value: String(order.documentType || (isInvoice ? "invoice" : "sales_order")).replaceAll("_", " ") },
          ]}
        />

        <InfoCard
          title="Totals"
          rows={[
            { label: "Subtotal", value: formatMoney(subtotal, currency) },
            { label: "Tax", value: order.taxAmount != null ? formatMoney(order.taxAmount, currency) : "—" },
            { label: "Shipping", value: order.shippingAmount != null ? formatMoney(order.shippingAmount, currency) : "—" },
            { label: "Total", value: formatMoney(total, currency) },
            { label: "Payment", value: String(order.paymentStatus || order.paymentMethod || "—").replaceAll("_", " ") },
          ]}
        />

        <InfoCard
          title="Fulfillment"
          rows={[
            { label: "Carrier", value: order.shippingCarrier || order.shippingService || "—" },
            { label: "Tracking", value: order.trackingNumber || "—" },
            { label: "Work order", value: workOrder?.workOrderNumber || "—" },
            { label: "Items", value: items.length },
          ]}
        />

        <IosCard>
          <Typography variant="subtitle2" fontWeight={900} sx={{ mb: 0.75 }}>
            Items
          </Typography>
          <Stack spacing={0.65}>
            {items.map((item, index) => {
              const inventoryItem = normalizeRelation(item.inventoryItem);
              const units = normalizeList(item.units);
              const qty = Number(item.quantity || 0);
              const unitPrice = item.unitPrice ?? inventoryItem?.unitPrice;
              const lineTotal = Number.isFinite(qty) && unitPrice != null ? Number(unitPrice) * qty : null;
              return (
                <Box key={item.documentId || item.id || index} sx={{ border: `1px solid ${iosColors.line}`, borderRadius: 1, p: 1 }}>
                  <Stack spacing={0.35}>
                    <Typography sx={{ color: iosColors.ink, fontSize: 14.5, fontWeight: 900, lineHeight: 1.2 }}>
                      {inventoryItem?.sku || item.sku || "Item"}
                    </Typography>
                    <Typography sx={{ color: iosColors.muted, fontSize: 12.75, lineHeight: 1.32 }}>
                      {inventoryItem?.name || item.description || item.notes || "No item description"}
                    </Typography>
                    <Stack direction="row" spacing={0.45} flexWrap="wrap" useFlexGap>
                      <Chip size="small" label={`Qty ${Number.isFinite(qty) ? qty : 0}`} />
                      <Chip size="small" label={`Unit ${formatMoney(unitPrice, currency)}`} />
                      <Chip size="small" label={`Line ${formatMoney(lineTotal, currency)}`} />
                      {units.length ? <Chip size="small" label={`${units.length} tag${units.length === 1 ? "" : "s"}`} /> : null}
                    </Stack>
                  </Stack>
                </Box>
              );
            })}
            {!items.length ? (
              <Typography sx={{ color: iosColors.muted, fontSize: 13 }}>No line items on this record.</Typography>
            ) : null}
          </Stack>
        </IosCard>

        {order.notes ? (
          <IosCard>
            <Typography variant="subtitle2" fontWeight={900} sx={{ mb: 0.75 }}>
              Notes
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
              {order.notes}
            </Typography>
          </IosCard>
        ) : null}
      </Stack>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullScreen>
        <IosPage>
          <MobileTopBar title={isInvoice ? "Edit Invoice" : "Edit Sales Order"} onBack={() => setEditOpen(false)} />
          <Stack spacing={1} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={draft.status}
                onChange={(event) => setDraft((prev) => ({ ...prev, status: event.target.value }))}
              >
                {STATUS_OPTIONS.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status.replaceAll("_", " ")}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Ship by"
              type="date"
              value={draft.shipBy}
              onChange={(event) => setDraft((prev) => ({ ...prev, shipBy: event.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Carrier"
              value={draft.shippingCarrier}
              onChange={(event) => setDraft((prev) => ({ ...prev, shippingCarrier: event.target.value }))}
            />
            <TextField
              label="Tracking"
              value={draft.trackingNumber}
              onChange={(event) => setDraft((prev) => ({ ...prev, trackingNumber: event.target.value }))}
            />
            <TextField
              label="Notes"
              value={draft.notes}
              multiline
              minRows={4}
              onChange={(event) => setDraft((prev) => ({ ...prev, notes: event.target.value }))}
            />
            <Stack direction="row" spacing={0.75}>
              <Button fullWidth variant="outlined" onClick={() => setEditOpen(false)} sx={secondaryActionSx}>
                Cancel
              </Button>
              <Button
                fullWidth
                variant="contained"
                disabled={saveMutation.isPending}
                onClick={() => saveMutation.mutate()}
                sx={primaryActionSx}
              >
                {saveMutation.isPending ? "Saving..." : "Save"}
              </Button>
            </Stack>
            {saveMutation.error ? (
              <Typography sx={{ color: iosColors.red, fontSize: 13, fontWeight: 800 }}>
                {saveMutation.error?.response?.data?.error?.message || saveMutation.error?.message || "Unable to save."}
              </Typography>
            ) : null}
          </Stack>
        </IosPage>
      </Dialog>
    </IosPage>
  );
}

SalesOrderDetailMobile.propTypes = {
  mode: PropTypes.oneOf(["sales", "invoice"]),
};
